import React, { memo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Stack,
  Typography,
  Button,
  Icon,
  TextField,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Divider,
  Card
} from 'investira.react.components';
import { BaseTemplate } from '../../../components/templates';
import withMessage from '../../../hoc/withMessage';
import { useAvaliacoesContext } from '../../../contexts/AvaliacoesContext';
import { AVALIACAO_MODELO, AVALIACAO_SESSOES_QUESTOES } from '../../../enums';
import Range from '../_components/Range';

const MAX_DISCRIMINACOES = 13;

const getOpcaoPorIndice = pIndice => `${pIndice}ª` || '';

const OPCOES_DISCRIMINACAO = Array(MAX_DISCRIMINACOES)
  .fill()
  .map((_, xIndex) => getOpcaoPorIndice(xIndex + 1));

const AvaliacoesSettingsQuestoesAdd = memo(props => {
  const navigate = useNavigate();

  const xSessoesQuestoes = Object.values(AVALIACAO_SESSOES_QUESTOES);
  const xModelos = Object.values(AVALIACAO_MODELO);

  const [questao, setQuestao] = useState('');
  const [sessaoId, setSessaoId] = useState(xSessoesQuestoes?.[0]?.sessao_id || '');
  const [peso, setPeso] = useState(null);
  const [modelosSelecionados, setModelosSelecionados] = useState([]);
  const [discriminacoes, setDiscriminacoes] = useState([
    {
      id: 1,
      opcao: OPCOES_DISCRIMINACAO[0],
      descricao: '',
      pontos: ''
    }
  ]);

  const { actions } = useAvaliacoesContext();

  const handleUpdateDiscriminacao = (id, field, value) => {
    setDiscriminacoes(pCurrent =>
      pCurrent.map(xDiscriminacao => {
        if (xDiscriminacao.id !== id) {
          return xDiscriminacao;
        }
        return { ...xDiscriminacao, [field]: value };
      })
    );
  };

  const handleAddDiscriminacao = () => {
    setDiscriminacoes(pCurrent => {
      if (pCurrent.length >= MAX_DISCRIMINACOES) {
        return pCurrent;
      }

      return [
        ...pCurrent,
        { id: Date.now(), opcao: getOpcaoPorIndice(pCurrent.length + 1), descricao: '', pontos: '' }
      ];
    });
  };

  const handleRemoveDiscriminacao = id => {
    setDiscriminacoes(pCurrent => {
      if (pCurrent.length <= 1) {
        return pCurrent;
      }

      return pCurrent
        .filter(xDiscriminacao => xDiscriminacao.id !== id)
        .map((xDiscriminacao, xIndex) => ({
          ...xDiscriminacao,
          opcao: getOpcaoPorIndice(xIndex + 1)
        }));
    });
  };

  const handleToggleModelo = pModeloId => {
    setModelosSelecionados(pCurrent =>
      pCurrent.includes(pModeloId)
        ? pCurrent.filter(xModeloId => xModeloId !== pModeloId)
        : [...pCurrent, pModeloId]
    );
  };

  const handleSave = () => {
    if (!questao.trim()) {
      props.onMessageError?.('Informe o título da questão.');
      return;
    }

    if (!sessaoId) {
      props.onMessageError?.('Selecione a sessão da questão.');
      return;
    }

    if (modelosSelecionados.length === 0) {
      props.onMessageError?.('Selecione ao menos um modelo.');
      return;
    }

    const xDiscriminacoes = discriminacoes
      .map(xItem => ({
        opcao: xItem.opcao?.trim(),
        descricao: xItem.descricao?.trim(),
        pontos: xItem.pontos === '' ? null : Number(xItem.pontos)
      }))
      .filter(xItem => xItem.opcao || xItem.descricao || xItem.pontos !== null);

    if (xDiscriminacoes.length === 0) {
      props.onMessageError?.('Adicione ao menos uma discriminação de resposta.');
      return;
    }

    const xPayload = {
      questao: questao.trim(),
      sessao_id: sessaoId,
      peso,
      modelos: modelosSelecionados,
      discriminacoes: xDiscriminacoes
    };

    console.log('nova questao', xPayload);
    props.onMessageSuccess?.('Questão criada com sucesso.');
    actions.handleChangeActiveSessaoId(sessaoId);
    navigate('/avaliacoes/settings/questoes');
  };

  return (
    <BaseTemplate
      title={'Nova Questão'}
      subtitle={'Avaliações'}
      description="Defina o peso de cada questão conforme sua relevância para a avaliação. Os pesos auxiliam na priorização dos critérios e permitem personalizar o impacto das respostas no resultado final."
      maxWidth={'xl'}
      headerRenderActions={
        <Stack direction="row" spacing={2}>
          <Button
            disabled={props.disabledButtons}
            variant="contained"
            color="primary"
            startIcon={<Icon iconName="upload" size={16} />}
            size="small"
            sx={{ px: 2 }}
            onClick={handleSave}>
            Salvar
          </Button>
        </Stack>
      }>
      <Divider />
      <Stack direction="row" gap={4} py={3}>
        <Stack component="main" flexGrow={1} pt={3} gap={2}>
          <Stack gap={2}>
            <TextField
              label="Título"
              placeholder="Ex.: Classificação de qualidade de gestão [agências de rating]"
              value={questao}
              onChange={pEvent => setQuestao(pEvent.target.value)}
              fullWidth
              InputLabelProps={{ shrink: true }}
              variant="outlined"
              multiline
              rows={4}
            />
          </Stack>

          <Divider />

          <Stack gap={2}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography color="textPrimary" variant="h6">
                Discriminação
              </Typography>
            </Stack>

            {discriminacoes.map((xDiscriminacao, xIndex) => (
              <Stack
                key={xDiscriminacao.id}
                gap={1.5}
                sx={{ border: 1, borderColor: 'divider', p: 2, borderRadius: 4 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="body2" color="textSecondary">
                    Opção {OPCOES_DISCRIMINACAO[xIndex]}
                  </Typography>

                  <Button
                    color="error"
                    variant="text"
                    size="small"
                    disabled={discriminacoes.length === 1}
                    onClick={() => handleRemoveDiscriminacao(xDiscriminacao.id)}
                    startIcon={<Icon iconName="remove" color="inherit" size={16} />}
                    px={2}>
                    Remover
                  </Button>
                </Stack>

                <Stack direction={{ xs: 'column', md: 'row' }} gap={2}>
                  <Select
                    label="Opção"
                    value={xDiscriminacao.opcao}
                    onChange={pEvent =>
                      handleUpdateDiscriminacao(xDiscriminacao.id, 'opcao', pEvent.target.value)
                    }
                    size="small"
                    sx={{ maxWidth: { md: 80 } }}
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                    variant="outlined">
                    {OPCOES_DISCRIMINACAO.map(xOpcao => (
                      <MenuItem key={xOpcao} value={xOpcao}>
                        {xOpcao}
                      </MenuItem>
                    ))}
                  </Select>
                  <TextField
                    label="Descrição"
                    placeholder="Discriminação da resposta"
                    value={xDiscriminacao.descricao}
                    onChange={pEvent =>
                      handleUpdateDiscriminacao(xDiscriminacao.id, 'descricao', pEvent.target.value)
                    }
                    size="small"
                    fullWidth
                    multiline
                    minRows={1}
                    InputLabelProps={{ shrink: true }}
                    variant="outlined"
                  />
                </Stack>

                <Stack>
                  <Range />
                </Stack>
              </Stack>
            ))}

            <Button
              variant="outlined"
              size="small"
              startIcon={<Icon iconName="insert" size={16} />}
              disabled={discriminacoes.length >= MAX_DISCRIMINACOES}
              onClick={handleAddDiscriminacao}>
              Adicionar resposta
            </Button>
            <Divider />
            {/* <QuestaoTipo /> */}
          </Stack>
        </Stack>

        <Stack
          component="sidebar"
          minWidth={{ lg: 320 }}
          maxWidth={{ lg: 420 }}
          gap={2}
          width="100%"
          sx={{
            position: { lg: 'sticky' },
            top: { lg: 16 },
            alignSelf: { lg: 'flex-start' }
          }}>
          <Card>
            <Stack gap={2} px={2} py={3}>
              <Stack gap={1}>
                <Typography
                  color="textPrimary"
                  variant="caption"
                  sx={{ textTransform: 'uppercase' }}>
                  SESSÃO
                </Typography>
                <Select
                  label="Sessão"
                  value={sessaoId}
                  onChange={pEvent => setSessaoId(pEvent.target.value)}
                  fullWidth
                  variant="outlined"
                  size="small"
                  inputProps={{ 'aria-label': 'Sessão da questão' }}>
                  {xSessoesQuestoes.map(xSessao => (
                    <MenuItem key={xSessao.sessao_id} value={xSessao.sessao_id}>
                      {xSessao.sessao}
                    </MenuItem>
                  ))}
                </Select>
              </Stack>
              <Divider />
              <Stack gap={1}>
                <Typography
                  color="textPrimary"
                  variant="caption"
                  sx={{ textTransform: 'uppercase' }}>
                  MODELOS
                </Typography>
                <Stack flexWrap="wrap" gap={0.5}>
                  <FormGroup>
                    {xModelos.map(xModelo => (
                      <FormControlLabel
                        key={xModelo.id}
                        control={
                          <Checkbox
                            checked={modelosSelecionados.includes(xModelo.id)}
                            onChange={() => handleToggleModelo(xModelo.id)}
                            size="small"
                          />
                        }
                        label={
                          <Typography color="textPrimary" variant="body2">
                            {xModelo.label}
                          </Typography>
                        }
                      />
                    ))}
                  </FormGroup>
                </Stack>
              </Stack>
            </Stack>
          </Card>
          <Stack spacing={2}>
            <Button
              disabled={props.disabledButtons}
              variant="contained"
              color="primary"
              startIcon={<Icon iconName="upload" size={16} />}
              size="small"
              sx={{ px: 2 }}
              onClick={handleSave}>
              Salvar
            </Button>
          </Stack>
        </Stack>
      </Stack>
    </BaseTemplate>
  );
});

AvaliacoesSettingsQuestoesAdd.displayName = 'AvaliacoesSettingsQuestoesAdd';

export default withMessage(AvaliacoesSettingsQuestoesAdd);
